import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAppDispatch } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import {
  type DeskPhone,
  type DeskPhones,
  type MyDeskPhoneSession,
  dialstackApi
} from "services/dialstack";

import { useDialstackSoftphone } from "../use-dialstack-softphone.hook";
import {
  loggedInSnackbar,
  loggedOutSnackbar,
  ringsOnAfterLogOut
} from "./desk-phone-copy";
import { rememberDesk } from "./use-remembered-desk.hook";

// Logging in on a desk phone (ENG-2293), as one state machine shared by the
// softphone picker and the Settings card. DialStack owns the session and the
// backend mirrors it; a login is final at the 200 — the phone reloads itself
// in a moment — so there is nothing to poll for.

export type DeskPhonePhase =
  | "confirming"
  | "loggingIn"
  | "done"
  // The provider said the phone changed under the request: the list was
  // re-read; the person looks again and decides again.
  | "changed"
  | "failed"
  | "confirmingLogOut"
  | "loggingOut"
  | "logOutFailed";

export type DeskPhoneRequest = {
  phone: DeskPhone;
  phase: DeskPhonePhase;
  // The backend's own words when it had some; null means use the fallback copy.
  error: string | null;
  since: number;
};

export type DeskPhonesController = {
  phones: DeskPhone[];
  me: DeskPhones["me"] | null;
  // The phone my calls ring on right now, if it is a desk phone.
  myPhone: DeskPhone | null;
  mySession: MyDeskPhoneSession | null;
  isLoading: boolean;
  isError: boolean;
  request: DeskPhoneRequest | null;
  // Open the confirm for a phone.
  select: (phone: DeskPhone) => void;
  // Go: from the confirm, or again after a failure.
  confirm: () => void;
  // Open the log-out confirm for the user's own session.
  requestLogOut: () => void;
  confirmLogOut: () => void;
  // Back to the list, whatever the phase (a request in flight carries on).
  cancel: () => void;
  // The picker closed by some other route (minimise, a ring, the Patients
  // tab): drop a confirm that would otherwise be stale next time, but never a
  // log-in or log-out that is in flight.
  dismiss: () => void;
  // Read the practice's phones again (after a failed read).
  refetch: () => void;
};

// "Front desk is yours." stays up this long, then the surface returns to its list.
export const DONE_LINGER_MS = 3000;

type ApiError = {
  status?: number;
  data?: { detail?: unknown; code?: unknown };
};

const errorDetail = (error: unknown): string | null => {
  const detail = (error as ApiError | null)?.data?.detail;
  return typeof detail === "string" && detail ? detail : null;
};

const isChanged = (error: unknown) =>
  (error as ApiError | null)?.status === 409 &&
  (error as ApiError).data?.code === "changed";

// The user's session names a phone the listing may not carry (a listing
// failure); enough to log out of it by name.
const placeholderPhone = (session: MyDeskPhoneSession): DeskPhone => ({
  id: session.device_id,
  name: session.device_name,
  type: "deskphone",
  location: null,
  online: null,
  owners: [],
  session: null,
  on_call: false,
  mine: true,
  own: false
});

export default function useDeskPhones({
  enabled = true
}: { enabled?: boolean } = {}): DeskPhonesController {
  const dispatch = useAppDispatch();
  const { setDialDevice } = useDialstackSoftphone();
  const { data, isLoading, isError, refetch } =
    dialstackApi.endpoints.getDeskPhones.useQuery(undefined, {
      skip: !enabled
    });
  const [logIn] = dialstackApi.endpoints.logInOnDeskPhone.useMutation();
  const [logOut] = dialstackApi.endpoints.logOutOfDeskPhone.useMutation();
  const [request, setRequest] = useState<DeskPhoneRequest | null>(null);
  // The latest request, for the callbacks that read it on a click.
  const requestRef = useRef<DeskPhoneRequest | null>(null);
  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  // `skip` keeps the last response around; gate what is read on the same flag.
  const phones = useMemo(
    () => (enabled ? (data?.phones ?? []) : []),
    [data, enabled]
  );
  const me = enabled ? (data?.me ?? null) : null;
  const mySession = me?.session ?? null;
  const myPhone = useMemo(
    () => phones.find((phone) => phone.mine) ?? null,
    [phones]
  );

  useEffect(() => {
    if (request?.phase !== "done") return undefined;
    const timer = window.setTimeout(() => {
      setRequest((current) => (current?.phase === "done" ? null : current));
    }, DONE_LINGER_MS);
    return () => window.clearTimeout(timer);
  }, [request]);

  const select = useCallback((phone: DeskPhone) => {
    setRequest({ phone, phase: "confirming", error: null, since: Date.now() });
  }, []);

  const cancel = useCallback(() => setRequest(null), []);

  const dismiss = useCallback(() => {
    setRequest((current) =>
      current?.phase === "loggingIn" || current?.phase === "loggingOut"
        ? current
        : null
    );
  }, []);

  const start = useCallback(
    (phone: DeskPhone) => {
      setRequest({ phone, phase: "loggingIn", error: null, since: Date.now() });
      logIn(phone.id)
        .unwrap()
        .then(
          (result) => {
            // My calls ring on a desk phone now — the one I logged in on, or
            // my own if I just went back to it — so dials go there, and this
            // browser remembers which phone sits at this desk.
            setDialDevice("deskphone");
            rememberDesk({ deviceId: phone.id, deviceName: phone.name });
            if (result.me.session)
              dispatch(setCustomMessage(loggedInSnackbar(phone.name)));
            else
              dispatch(
                setCustomMessage(`Your calls ring on ${phone.name} again.`)
              );
            dispatch(setOpen(true));
            setRequest((current) =>
              current?.phone.id === phone.id
                ? { ...current, phase: "done", error: null, since: Date.now() }
                : current
            );
          },
          (error: unknown) => {
            if (isChanged(error)) refetch();
            setRequest((current) =>
              current?.phone.id === phone.id
                ? {
                    ...current,
                    phase: isChanged(error) ? "changed" : "failed",
                    error: errorDetail(error)
                  }
                : current
            );
          }
        );
    },
    [logIn, setDialDevice, dispatch, refetch]
  );

  const confirm = useCallback(() => {
    const current = requestRef.current;
    if (current?.phase === "confirming" || current?.phase === "failed")
      start(current.phone);
  }, [start]);

  const requestLogOut = useCallback(() => {
    if (!mySession) return;
    setRequest({
      phone:
        phones.find((phone) => phone.id === mySession.device_id) ??
        placeholderPhone(mySession),
      phase: "confirmingLogOut",
      error: null,
      since: Date.now()
    });
  }, [mySession, phones]);

  const confirmLogOut = useCallback(() => {
    const current = requestRef.current;
    if (
      current?.phase !== "confirmingLogOut" &&
      current?.phase !== "logOutFailed"
    )
      return;
    const { phone } = current;
    setRequest({ ...current, phase: "loggingOut" });
    logOut()
      .unwrap()
      .then(
        (result) => {
          setDialDevice(result.me.own_phone_id ? "deskphone" : "softphone");
          dispatch(
            setCustomMessage(
              loggedOutSnackbar(
                phone.name,
                ringsOnAfterLogOut(result.me, result.phones)
              )
            )
          );
          dispatch(setOpen(true));
          setRequest(null);
        },
        (error: unknown) => {
          setRequest((c) =>
            c?.phone.id === phone.id
              ? { ...c, phase: "logOutFailed", error: errorDetail(error) }
              : c
          );
        }
      );
  }, [logOut, setDialDevice, dispatch]);

  return {
    phones,
    me,
    myPhone,
    mySession,
    isLoading: enabled && isLoading,
    isError: enabled && isError,
    request,
    select,
    confirm,
    requestLogOut,
    confirmLogOut,
    cancel,
    dismiss,
    refetch: () => {
      refetch();
    }
  };
}
